import React, { useEffect, useState, useRef } from "react";
import { Recurrente } from "../../types/recurrente";
import { upsertRecurrente } from "../../services/General/recurrenteService";
import { fetchBuildings } from "../../services/Administracion/buildingsService";
import { useAuth } from "../../context/AuthContext";
import PersonaSearch from "../shared/PersonaSearch";
import Swal from "sweetalert2";
import { Building } from "../../types/building";
import CreatePersonaModal from "../shared/CrearPersonaModal";
import { PersonaSQL } from "../../types/PersonaSQL";

interface RecurrenteModalProps {
  recurrente: Recurrente;
  onClose: () => void;
  onSave: (recurrente: Recurrente, isNew: boolean) => void;
}

const RecurrenteModal: React.FC<RecurrenteModalProps> = ({
  recurrente,
  onClose,
  onSave,
}) => {
  const { user: userContext } = useAuth();
  const [formData, setFormData] = useState({
    guest_cedula: recurrente.guest_cedula,
    guest_name: recurrente.guest_name,
    guest_email: recurrente.guest_email || "",
    host_email: recurrente.host_email,
    building_id: recurrente.building_id || null,
    motivo: recurrente.motivo,
    frecuencia: recurrente.frecuencia || "",
    dia_preferido: recurrente.dia_preferido || "",
    fecha_fin: recurrente.fecha_fin ? recurrente.fecha_fin.split("T")[0] : "",
    max_visitas_sin_cita: recurrente.max_visitas_sin_cita,
    is_active: recurrente.is_active,
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const motivoInputRef = useRef<HTMLInputElement>(null);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [selectedBuilding, setSelectedBuilding] = useState<number | null>(null);
  const [showCreatePersonaModal, setShowCreatePersonaModal] = useState(false);
  const [pendingSearchQuery, setPendingSearchQuery] = useState("");
  const [personaSearchResetKey, setPersonaSearchResetKey] = useState(0);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        //carga Edificios
        const buildingsData = await fetchBuildings();
        setBuildings(buildingsData);
      } catch (error: any) {
        console.log("Error cargando datos iniciales:", error.message);
        //setError('Error al cargar los datos iniciales');
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    console.log("Validando el formulario", formData);
    // Validar formulario
    const isValid =
      formData.guest_cedula.trim() !== "" &&
      formData.guest_name.trim() !== "" &&
      formData.host_email.trim() !== "" &&
      formData.building_id != null &&
      formData.building_id > 0 &&
      formData.motivo.trim() !== "" &&
      Object.keys(errors).length === 0;

    setIsFormValid(isValid);
    setSelectedBuilding(formData.building_id);
    console.log("Errores: ", Object.keys(errors));
    console.log("Formulario válido:", isValid);
  }, [formData, errors]);

  useEffect(() => {
    // Enfocar campo motivo después de seleccionar persona
    if (formData.guest_name && motivoInputRef.current) {
      motivoInputRef.current.focus();
    }
  }, [formData.guest_name]);

  const validateField = (field: string, value: any) => {
    let errorMessage = "";

    if (typeof value === "string") {
      if (value.trim() === "") {
        if (
          field === "guest_cedula" ||
          field === "guest_name" ||
          field === "host_email" ||
          field === "motivo" ||
          field === "guest_email"
        ) {
          errorMessage = "Este campo es obligatorio";
        }
      }

      if (field === "host_email" && value.trim() !== "") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          errorMessage = "El correo electrónico no es válido";
        }
      }

      if (field === "guest_email" && value.trim() !== "") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          errorMessage = "El correo electrónico no es válido";
        }
      }

      if (field === "max_visitas_sin_cita" && parseInt(value) < 1) {
        errorMessage = "Debe ser al menos 1";
      }
    }

    if (field === "building_id") {
      if (!value || value === "" || value === 0) {
        errorMessage = "Debe seleccionar un edificio";
      }
    }

    setErrors((prev) => {
      const newErrors = { ...prev };

      if (errorMessage) {
        // Hay error: lo agregamos o actualizamos
        newErrors[field] = errorMessage;
      } else {
        // No hay error: eliminamos la entrada del objeto
        delete newErrors[field];
      }

      return newErrors;
    });
  };

  const handlePersonaSelect = (persona: any) => {
    const nombreCompleto =
      `${persona.nombre} ${persona.apellido1 || ""} ${persona.apellido2 || ""}`.trim();
    setFormData((prev) => ({
      ...prev,
      guest_cedula: persona.identificador,
      guest_name: nombreCompleto,
      guest_email: prev.guest_email, // Mantener email si ya tenía
    }));
    // Limpiar error de cédula y nombre si existía
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.guest_cedula;
      delete newErrors.guest_name;
      return newErrors;
    });
  };

  // Manejador para cuando se crea exitosamente una persona
  const handlePersonaCreada = async (nuevaPersona: PersonaSQL) => {
    // La misma lógica que handlePersonaSelect pero sin verificar veto
    const nombreCompleto =
      `${nuevaPersona.nombre} ${nuevaPersona.apellido1} ${nuevaPersona.apellido2}`.trim();

    setFormData((prev) => ({
      ...prev,
      guest_cedula: nuevaPersona.identificador,
      guest_name: nombreCompleto,
    }));

    setPersonaSearchResetKey((prev) => prev + 1);

    // Mostrar mensaje de éxito
    Swal.fire({
      title: "¡Persona creada!",
      text: `${nombreCompleto} ha sido agregada al sistema`,
      icon: "success",
      timer: 2000,
    });

    // Opcional: verificar si está vetada (aunque recién creada no debería)
    // pero podrías dejarlo por si acaso
    //setPersonaVetada({ vetado: false });
  };

  const handleSubmit = async () => {
    // Validar todos los campos antes de enviar
    if (!isFormValid) {
      Swal.fire({
        title: "Validación",
        text: "Por favor complete todos los campos obligatorios correctamente",
        icon: "warning",
        confirmButtonText: "Entendido",
      });
      return;
    }

    setLoading(true);
    console.log("Datos en el formulario", formData);
    try {
      const isNew = recurrente.id === 0;

      const recurrenteData = {
        ...formData,
        building_id: selectedBuilding,
      };

      const savedRecurrente = await upsertRecurrente(
        recurrenteData,
        userContext?.User.email || "",
        isNew ? undefined : recurrente.id,
      );
      Swal.fire({
        title: "¡Éxito!",
        text: `Visita recurrente ${isNew ? "creada" : "actualizada"} correctamente`,
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      onSave(savedRecurrente, isNew);
    } catch (error: any) {
      console.error("Error guardando recurrente:", error);
      Swal.fire({
        title: "Error",
        text:
          error.response?.data?.error ||
          "Error al guardar la visita recurrente",
        icon: "error",
        confirmButtonText: "Cerrar",
      });
    } finally {
      setLoading(false);
    }
  };

  // Manejador para cuando no se encuentra la persona
  const handlePersonaNotFound = (query: string) => {
    setPendingSearchQuery(query); //Guarda el texto que uso el usuario
    setShowCreatePersonaModal(true); //Abre el modal
  };

  return (
    <div
      className="modal show d-block"
      tabIndex={-1}
      role="dialog"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        role="document"
      >
        <div className="modal-content shadow-lg">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="fas fa-repeat me-2"></i>
              {recurrente.id
                ? "Editar Visita Recurrente"
                : "Nueva Visita Recurrente"}
              {recurrente.id > 0 && (
                <span className="ms-2 small">(ID: {recurrente.id})</span>
              )}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
            ></button>
          </div>

          <div className="modal-body">
            {/* Buscador de persona (solo para nuevo, o deshabilitado si ya existe) */}
            <div className="mb-3">
              <label className="form-label fw-bold">
                Buscar Visitante{" "}
                {!recurrente.id && <span className="text-danger">*</span>}
              </label>
              {recurrente.id ? (
                <div className="alert alert-info py-2">
                  <strong>{recurrente.guest_name}</strong> (
                  {recurrente.guest_cedula})
                </div>
              ) : (
                <>
                  <PersonaSearch
                    onPersonaSelect={handlePersonaSelect}
                    onPersonaNotFound={handlePersonaNotFound}
                    value={formData.guest_cedula}
                    resetKey={personaSearchResetKey}
                  />
                  {!recurrente.id && formData.guest_name && (
                    <div className="alert alert-success py-2 mt-2">
                      <i className="fas fa-check-circle me-2"></i>
                      <strong>Visitante seleccionado:</strong>{" "}
                      {formData.guest_name} ({formData.guest_cedula})
                    </div>
                  )}
                  {errors.guest_cedula && (
                    <div className="text-danger small mt-1">
                      {errors.guest_cedula}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Datos del visitante (solo lectura si ya existe) */}
            {recurrente.id > 0 && (
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label small">Cédula</label>
                  <input
                    type="text"
                    className="form-control"
                    value={recurrente.guest_cedula}
                    disabled
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Nombre Completo</label>
                  <input
                    type="text"
                    className="form-control"
                    value={recurrente.guest_name}
                    disabled
                  />
                </div>
              </div>
            )}

            {/* Email del visitante */}
            <div className="mb-3">
              <label className="form-label">
                Email del Visitante <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                className={`form-control ${errors.guest_email ? "is-invalid" : ""}`}
                value={formData.guest_email}
                onChange={(e) => {
                  setFormData({ ...formData, guest_email: e.target.value });
                  validateField("guest_email", e.target.value);
                }}
                onBlur={(e) => validateField("guest_email", e.target.value)}
                placeholder="correo@ejemplo.com"
              />
              {errors.guest_email && (
                <div className="invalid-feedback">{errors.guest_email}</div>
              )}
            </div>

            <div className="row">
              {/* Anfitrión */}
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Anfitrión <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  className={`form-control ${errors.host_email ? "is-invalid" : ""}`}
                  value={formData.host_email}
                  onChange={(e) =>
                    setFormData({ ...formData, host_email: e.target.value })
                  }
                  onBlur={(e) => validateField("host_email", e.target.value)}
                  placeholder="anfitrion@empresa.com"
                  disabled //={!!recurrente.id} // No permitir cambiar anfitrión en edición
                />
                {errors.host_email && (
                  <div className="invalid-feedback">{errors.host_email}</div>
                )}
              </div>
              {/* Edificio */}
              <div className="col-md-6 mb-3">
                <label htmlFor="" className="form-label small fw-bold">
                  Edificio <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select form-select-sm ${errors.building_id ? "is-invalid" : ""}`}
                  value={formData.building_id?.toString() || ""}
                  // onChange={(e) => setSelectedBuilding(e.target.value ? Number(e.target.value) : null)}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      building_id: e.target.value
                        ? parseInt(e.target.value)
                        : null,
                    })
                  }
                  onBlur={(e) => validateField("building_id", e.target.value)}
                  required
                >
                  <option value="">Seleccionar edificio</option>
                  {buildings.map((building) => (
                    <option
                      key={building.building_id}
                      value={building.building_id}
                    >
                      {building.building_name}
                    </option>
                  ))}
                </select>
                {errors.building_id && (
                  <div className="invalid-feedback">{errors.building_id}</div>
                )}
              </div>
            </div>

            {/* Motivo */}
            <div className="mb-3">
              <label className="form-label">
                Motivo de la recurrencia <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.motivo ? "is-invalid" : ""}`}
                value={formData.motivo}
                onChange={(e) =>
                  setFormData({ ...formData, motivo: e.target.value })
                }
                onBlur={(e) => validateField("motivo", e.target.value)}
                placeholder="Ej: Entrega de materiales cada semana"
                ref={motivoInputRef}
              />
              {errors.motivo && (
                <div className="invalid-feedback">{errors.motivo}</div>
              )}
            </div>

            {/* Frecuencia y Día preferido */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Frecuencia</label>
                <select
                  className="form-select"
                  value={formData.frecuencia}
                  onChange={(e) =>
                    setFormData({ ...formData, frecuencia: e.target.value })
                  }
                >
                  <option value="">Sin frecuencia definida</option>
                  <option value="SEMANAL">Semanal</option>
                  <option value="QUINCENAL">Quincenal</option>
                  <option value="MENSUAL">Mensual</option>
                  <option value="OTRO">Otro</option>
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Día preferido</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.dia_preferido}
                  onChange={(e) =>
                    setFormData({ ...formData, dia_preferido: e.target.value })
                  }
                  placeholder="Ej: LUNES, 15"
                />
                <small className="text-muted">
                  Día de la semana o número de mes
                </small>
              </div>
            </div>

            {/* Fecha de fin y límite de visitas */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Fecha de fin (opcional)</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.fecha_fin}
                  onChange={(e) =>
                    setFormData({ ...formData, fecha_fin: e.target.value })
                  }
                />
                <small className="text-muted">
                  Dejar vacío = sin fecha de fin
                </small>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Máx visitas sin cita</label>
                <input
                  type="number"
                  className={`form-control ${errors.max_visitas_sin_cita ? "is-invalid" : ""}`}
                  value={formData.max_visitas_sin_cita}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_visitas_sin_cita: parseInt(e.target.value) || 1,
                    })
                  }
                  onBlur={(e) =>
                    validateField("max_visitas_sin_cita", e.target.value)
                  }
                  min="1"
                  max="50"
                />
                {errors.max_visitas_sin_cita && (
                  <div className="invalid-feedback">
                    {errors.max_visitas_sin_cita}
                  </div>
                )}
                <small className="text-muted">Límite por mes</small>
              </div>
            </div>

            {/* Estado Activo/Inactivo */}
            <div className="mb-3">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                  id="activeSwitch"
                />
                <label className="form-check-label" htmlFor="activeSwitch">
                  {formData.is_active ? "Activa" : "Inactiva"}
                </label>
              </div>
            </div>

            {/* Mostrar contador si es edición */}
            {recurrente.id > 0 && recurrente.visitas_realizadas > 0 && (
              <div className="alert alert-info py-2 mt-2">
                <i className="fas fa-info-circle me-2"></i>
                <strong>Visitas realizadas este mes:</strong>{" "}
                {recurrente.visitas_realizadas}/
                {recurrente.max_visitas_sin_cita}
                {recurrente.ultima_visita && (
                  <div>
                    <small>
                      Última visita:{" "}
                      {new Date(recurrente.ultima_visita).toLocaleDateString(
                        "es-ES",
                      )}
                    </small>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={!isFormValid || loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Guardando...
                </>
              ) : (
                "Guardar"
              )}
            </button>
          </div>
        </div>
      </div>
      {/* Modal de creación de persona */}
      <CreatePersonaModal
        show={showCreatePersonaModal}
        onHide={() => setShowCreatePersonaModal(false)}
        searchQuery={pendingSearchQuery}
        onPersonaCreada={handlePersonaCreada}
      />
    </div>
  );
};

export default RecurrenteModal;
